import { s } from '@/theme/tokens';
import { ToggleGroup, ToggleItem } from '@/ui';
import { Box, Cancel, IconButton, Text, Tooltip } from '@/ui/primitives';
import { EventOutlinedIcon } from '@/ui/primitives/icons';
import { toolbarControlRadius } from '../tableSystem/tableToolbar.helpers';
import type { QualityRange } from '@/features/quality/hooks';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { MouseEvent, ReactElement } from 'react';
type MetricsToolbarDateControlsProps = {
  cloneTooltipSlotProps: {
    tooltip: {
      sx: {
        fontSize: string;
        lineHeight: number;
      };
    };
  };
  dateQuickFilter: DateQuickFilter;
  dateRangeLabel: string;
  hasActiveToolbarState: boolean;
  hasCustomDateRange: boolean;
  hideDateRangeLabel: boolean;
  includeLatestQuickFilter: boolean;
  dateQuickFilterOptions?: QualityRange[];
  showCustomDateRange: boolean;
  onDateQuickFilterChange: (next: DateQuickFilter) => void;
  onOpenCustomDateFilters: (event: MouseEvent<HTMLElement>) => void;
  onResetToolbarFilters: () => void;
  preserveWindowScroll: (action: () => void) => void;
  toolbarControlSize: number;
  toolbarIconButtonSx: object | object[];
  toolbarIconCircleSx: (selected: boolean) => object;
  toolbarIconTooltipDelayMs: number;
};

const quickFilterTextSx = {
  fontSize: (muiTheme: { typography: { pxToRem: (value: number) => string } }) =>
    muiTheme.typography.pxToRem(13),
  lineHeight: 1.4,
} as const;

const dateRangeLabelSx = {
  color: 'text.secondary',
  fontSize: (muiTheme: { typography: { pxToRem: (value: number) => string } }) =>
    muiTheme.typography.pxToRem(13),
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
} as const;

export const MetricsToolbarDateControls = ({
  cloneTooltipSlotProps,
  dateQuickFilter,
  dateRangeLabel,
  hasActiveToolbarState,
  hasCustomDateRange,
  hideDateRangeLabel,
  includeLatestQuickFilter,
  dateQuickFilterOptions,
  showCustomDateRange,
  onDateQuickFilterChange,
  onOpenCustomDateFilters,
  onResetToolbarFilters,
  preserveWindowScroll,
  toolbarControlSize,
  toolbarIconButtonSx,
  toolbarIconCircleSx,
  toolbarIconTooltipDelayMs,
}: MetricsToolbarDateControlsProps): ReactElement => (
  <>
    {dateRangeLabel && !hideDateRangeLabel ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Text role="body" sx={dateRangeLabelSx}>
          {dateRangeLabel}
        </Text>
      </Box>
    ) : null}
    <ToggleGroup
      size="small"
      value={hasCustomDateRange ? 'custom' : dateQuickFilter}
      onChange={(_event, nextValue: DateQuickFilter | 'custom' | null) => {
        if (!nextValue || nextValue === 'custom') {
          return;
        }
        preserveWindowScroll(() => {
          onDateQuickFilterChange(nextValue);
        });
      }}
      sx={{
        flexShrink: 0,
        ml: s('xs'),
        borderRadius: `${toolbarControlRadius} !important`,
        '& .MuiToggleButton-root': {
          minWidth: 0,
          px: s('sm'),
          py: s('none'),
          height: `${toolbarControlSize}px`,
          borderRadius: '0 !important',
        },
        '& .MuiToggleButton-root:first-of-type': {
          borderTopLeftRadius: `${toolbarControlRadius} !important`,
          borderBottomLeftRadius: `${toolbarControlRadius} !important`,
        },
        '& .MuiToggleButton-root:last-of-type': {
          borderTopRightRadius: `${toolbarControlRadius} !important`,
          borderBottomRightRadius: `${toolbarControlRadius} !important`,
        },
        '& .MuiToggleButton-root:not(.Mui-selected)': {
          backgroundColor: 'var(--surface-inset)',
        },
        '& .MuiToggleButton-root:not(.Mui-selected):hover, & .MuiToggleButton-root:not(.Mui-selected).Mui-focusVisible, & .MuiToggleButton-root:not(.Mui-selected):active':
          {
            backgroundColor: 'var(--surface-inset)',
          },
        '& .MuiToggleButton-root.Mui-selected:hover, & .MuiToggleButton-root.Mui-selected.Mui-focusVisible, & .MuiToggleButton-root.Mui-selected:active':
          {
            backgroundColor: 'primary.main',
          },
        '& .MuiToggleButton-root.Mui-selected': {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        },
        '& .MuiToggleButton-root.Mui-selected .MuiTypography-root': {
          color: 'inherit',
        },
        '& .MuiToggleButton-root.Mui-selected .MuiSvgIcon-root': {
          color: 'inherit',
        },
      }}
    >
      {includeLatestQuickFilter && dateQuickFilterOptions?.includes('latest') !== false ? (
        <ToggleItem value="latest">
          <Text role="body" component="span" sx={quickFilterTextSx}>
            Latest
          </Text>
        </ToggleItem>
      ) : null}
      {(['7d', '14d', '30d', '90d'] satisfies DateQuickFilter[]).map((option) =>
        dateQuickFilterOptions?.includes(option) === false ? null : (
          <ToggleItem key={option} value={option}>
            <Text role="body" component="span" sx={quickFilterTextSx}>
              {option}
            </Text>
          </ToggleItem>
        ),
      )}
      {showCustomDateRange ? (
        <Tooltip
          title="Custom Dates"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={cloneTooltipSlotProps}
        >
          <ToggleItem
            value="custom"
            aria-label="Open custom date filters"
            onClick={onOpenCustomDateFilters}
            sx={{
              minWidth: 0,
              width: `${toolbarControlSize}px`,
              px: s('none'),
            }}
          >
            <EventOutlinedIcon
              sx={{
                width: '20px !important',
                height: '20px !important',
              }}
            />
          </ToggleItem>
        </Tooltip>
      ) : null}
    </ToggleGroup>
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
    >
      <Tooltip
        title="Reset"
        arrow
        enterDelay={toolbarIconTooltipDelayMs}
        slotProps={cloneTooltipSlotProps}
      >
        <Box component="span">
          <IconButton
            size="medium"
            aria-label="Reset chart filters"
            onClick={onResetToolbarFilters}
            disabled={!hasActiveToolbarState}
            sx={[toolbarIconButtonSx, hasActiveToolbarState ? { color: 'primary.main' } : null]}
          >
            <Box sx={toolbarIconCircleSx(false)}>
              <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
            </Box>
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  </>
);
